import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { formatCalendarDay, toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { de } from 'date-fns/locale';
import { html } from 'lit';
import { SepaExportSettings } from '../entities/SepaExportSettings.js';

const SepaExportDialogContent = stateful(
  (props: {
    groupID: string;
    groupName: string;
    resolve: (settings: SepaExportSettings | null) => void;
    reject: () => void;
  }) => {
    const [stichtag, setStichtag] = useState<Date>(todayAsCalendarDay());
    const [lock, setLock] = useState(false);

    return html`
      <div class="dialog-container sepa-export-dialog">
        ${DialogHeader({ title: `SEPA-Export · ${props.groupName}`, close: props.reject, closeLabel: 'Dialog schließen' })}

        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Stichtag</span>
          ${DateInput({
            value: stichtag,
            type: 'date',
            placeholder: 'Stichtag',
            placeholderMode: 'prefix',
            onPicked: date => {
              if (date) setStichtag(toCalendarDay(date));
            },
          })}
          <span class="text-sm text-base-content/70">
            Eingezogen wird, was am ${formatCalendarDay(stichtag, 'dd.MM.yyyy')} läuft und fällig ist. Gebucht wird auf
            ${formatCalendarDay(stichtag, 'MMMM yyyy', { locale: de })}.
          </span>
        </div>

        <div class="flex flex-col gap-2">
          <label class="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3">
            <input
              type="radio"
              class="radio radio-sm mt-0.5"
              name="sepa-export-mode"
              .checked=${!lock}
              @change=${() => setLock(false)}
            />
            <span class="flex flex-col">
              <span class="font-medium">Nur Vorschau</span>
              <span class="text-sm text-base-content/70">Erzeugt die Dateien und ändert nichts.</span>
            </span>
          </label>
          <label class="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3">
            <input
              type="radio"
              class="radio radio-sm mt-0.5"
              name="sepa-export-mode"
              .checked=${lock}
              @change=${() => setLock(true)}
            />
            <span class="flex flex-col">
              <span class="font-medium">Exportieren und sperren</span>
              <span class="text-sm text-base-content/70">
                Erzeugt die Dateien und sperrt die Gruppe, bis eine ihrer Buchungen wieder fällig ist. Einmalige Mandate
                gelten danach als abgerechnet; ein zweiter Export für denselben Zeitraum wird abgelehnt.
              </span>
            </span>
          </label>
        </div>

        <div class="flex justify-end gap-2">
          <button
            class="btn btn-primary btn-lg"
            @click=${() => props.resolve(new SepaExportSettings({ groupID: props.groupID, stichtag, lock }))}
          >
            ${lock ? 'Exportieren und sperren' : 'Vorschau erstellen'}
          </button>
        </div>
      </div>
    `;
  },
);

export const openSepaExportDialog = async (groupID: string, groupName: string) => {
  return showDialog<SepaExportSettings | null>({
    content: (resolve, reject) => SepaExportDialogContent({ groupID, groupName, resolve, reject }),
    modalBoxClasses: 'flex flex-col flex-grow min-w-[520px]',
    modal: true,
  });
};
